import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup } from 'react-leaflet';
import L from 'leaflet';

import { db } from '../firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import fireIconUrl from './fire-icon.png';
import smokeIconUrl from './smoke-icon.png';
import satelliteIconUrl from './satellite-icon.png';

const calculateFireRisk = (temp, humidity, wind) => {
    const score = (temp * wind) / (humidity || 1); 
    let level = "Düşük";
    let color = "bg-emerald-500 shadow-emerald-500/40";
    let description = "Yangın riski şu an düşük seviyede.";

    if (score > 2.0) {
        level = "YÜKSEK RİSK";
        color = "bg-red-600 shadow-red-600/40";
        description = "DİKKAT! Yangın riski kritik seviyede.";
    } else if (score > 0.8) {
        level = "ORTA RİSK";
        color = "bg-orange-500 shadow-orange-500/40";
        description = "Tedbirli olunmalı, risk artışta.";
    }
    return { level, color, description, score: Math.min(Math.round(score * 10), 100) };
};

const fireIcon = new L.Icon({
  iconUrl: fireIconUrl, iconSize: [35, 35], iconAnchor: [17, 35], popupAnchor: [0, -35], className: 'fire-pulse-animation'
});

const smokeIcon = new L.Icon({
  iconUrl: smokeIconUrl, iconSize: [35, 35], iconAnchor: [17, 35], popupAnchor: [0, -35], className: 'smoke-pulse-animation'
});

const satelliteIcon = new L.Icon({
  iconUrl: satelliteIconUrl, iconSize: [35, 35], iconAnchor: [17, 35], popupAnchor: [0, -35]
});

const resolvedIcon = new L.DivIcon({
  html: `<div style="background-color: #10b981; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.3); font-size: 14px;">✓</div>`,
  className: 'resolved-icon',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const OPENWEATHER_API_KEY = "e673fb8307d05b9205f8b5281e74fce6"; 

const getConfidenceText = (confidenceCode) => {
  if (confidenceCode === 'h') return 'Yüksek';
  if (confidenceCode === 'n') return 'Normal';
  if (confidenceCode === 'l') return 'Düşük';
  return confidenceCode;
};

const MapComponent = () => {
  const [reports, setReports] = useState([]);
  const [nasaFireData, setNasaFireData] = useState([]);
  const [weather, setWeather] = useState(null);
  const [risk, setRisk] = useState(null);
  const [locationName, setLocationName] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "ihbarlar"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const reportsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.konum && data.konum.enlem && data.konum.boylam && data.durum !== 'Asılsız') {
            reportsData.push({ 
                id: doc.id, 
                ...data,
                konum: { enlem: parseFloat(data.konum.enlem), boylam: parseFloat(data.konum.boylam) }
            });
        }
      });
      setReports(reportsData);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchNasaData = async () => {
      try {
        // Doğrudan NASA API Adresi
const url = "https://firms.modaps.eosdis.nasa.gov/api/area/csv/b02a8566d3a625c83a6670ba20245c3b/VIIRS_SNPP_NRT/26,36,45,42/5";
        const response = await fetch(url);
        if (!response.ok) return; 
        const csvText = await response.text();
        const lines = csvText.trim().split('\n');
        if (lines.length < 2) { setNasaFireData([]); return; }
        const headers = lines[0].split(',');
        const fireData = lines.slice(1).map((line, index) => {
          const data = line.split(',');
          const latIndex = headers.indexOf('latitude');
          const lonIndex = headers.indexOf('longitude');
          const confIndex = headers.indexOf('confidence');
          if (latIndex === -1 || lonIndex === -1) return null;
          return { id: `nasa-${index}`, latitude: parseFloat(data[latIndex]), longitude: parseFloat(data[lonIndex]), confidence: confIndex > -1 ? data[confIndex] : 'n' };
        }).filter(item => item && !isNaN(item.latitude) && !isNaN(item.longitude) && item.confidence === 'h');
        setNasaFireData(fireData);
      } catch (error) {
        console.error("NASA Hatası:", error);
      }
    };
    fetchNasaData();
  }, []);

  useEffect(() => {
    const fetchWeatherAndRisk = async (lat, lon) => {
        const url = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&lang=tr&appid=${OPENWEATHER_API_KEY}`;
        try {
            const response = await fetch(url);
            const data = await response.json();
            if (data.main) {
                setWeather(data); setLocationName(data.name);
                const riskData = calculateFireRisk(data.main.temp, data.main.humidity, data.wind.speed);
                setRisk(riskData);
            }
        } catch (error) { console.error("Hava durumu alınamadı:", error); }
    };

    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => fetchWeatherAndRisk(position.coords.latitude, position.coords.longitude),
            () => fetchWeatherAndRisk(39.9334, 32.8597)
        );
    } else {
        fetchWeatherAndRisk(39.9334, 32.8597);
    }
  }, []);

  const position = [39.9334, 32.8597]; 
  const zoomLevel = 6;

  const getIconForReport = (report) => {
      if (report.durum === 'Müdahale Edildi') return resolvedIcon;
      return report.tip === 'Duman' ? smokeIcon : fireIcon;
  };

  return (
    <div className="fixed inset-0 w-full h-full z-0 font-sans">
        <MapContainer center={position} zoom={zoomLevel} style={{ height: "100%", width: "100%" }} zoomControl={false}>
        
        <LayersControl position="bottomleft">
            <LayersControl.BaseLayer checked name="Sokak Haritası">
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Uydu Görüntüsü">
            <TileLayer attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            </LayersControl.BaseLayer>

            <LayersControl.Overlay checked name="Kullanıcı İhbarları">
            <LayerGroup>
                {reports.map(report => (
                <Marker 
                    key={report.id} 
                    position={[report.konum.enlem, report.konum.boylam]}
                    icon={getIconForReport(report)}
                >
                    <Popup className="font-sans custom-popup" maxWidth={220} minWidth={200}>
                        <div style={{ width: '200px', overflow: 'hidden' }}>
                            <div className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 inline-block rounded-md mb-2 text-white 
                                ${report.durum === 'Müdahale Edildi' ? 'bg-emerald-500' : 'bg-red-600'}`}>
                                {report.durum === 'Müdahale Edildi' ? 'SÖNDÜRÜLDÜ' : report.tip}
                            </div>
                            <br />
                            
                            <div className="text-[11px] leading-relaxed text-slate-600 mt-1">
                                <b className="text-slate-800">Tarih:</b> {report.zaman ? new Date(report.zaman.seconds * 1000).toLocaleString('tr-TR') : 'Belirtilmemiş'} <br />
                                <b className="text-slate-800">Bildiren:</b> {report.adSoyad || 'Bilinmiyor'} <br />
                                
                                <b className="text-slate-800">Telefon:</b>{' '}
                                {report.telefon && report.telefon !== "Belirtilmemiş" ? (
                                    <a 
                                        href={`tel:+90${report.telefon.replace(/\D/g, '').slice(-10)}`} 
                                        className="text-blue-600 font-bold hover:underline"
                                        title="Aramak için tıkla"
                                    >
                                        {report.telefon}
                                    </a>
                                ) : (
                                    'Bilinmiyor'
                                )} <br />
                                
                                <b className="text-slate-800">Durum:</b>{' '}
                                <span className={`font-bold ${
                                    report.durum === 'Müdahale Edildi' ? 'text-emerald-600' : 
                                    report.durum === 'Ekiplere İletildi' ? 'text-blue-600' : 
                                    'text-amber-500'
                                }`}>
                                    {report.durum || 'Bekliyor'}
                                </span>
                            </div>
                            
                            {report.medyaUrl && (
                                <div style={{ marginTop: '12px', position: 'relative' }}>
                                    {report.medyaTipi === 'video' ? (
                                        <video 
                                            controls 
                                            src={report.medyaUrl} 
                                            style={{ width: '100%', maxHeight: '120px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#000' }} 
                                        />
                                    ) : (
                                        <a href={report.medyaUrl} target="_blank" rel="noreferrer" title="Tam boyutu gör" style={{ display: 'block', overflow: 'hidden', borderRadius: '8px', position: 'relative' }}>
                                            <img 
                                                src={report.medyaUrl} 
                                                alt="İhbar Kanıtı" 
                                                style={{ width: '100%', height: '120px', objectFit: 'cover', border: '1px solid #e2e8f0' }} 
                                            />
                                            <div style={{ position: 'absolute', bottom: '4px', right: '4px', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                TIKLA BÜYÜT
                                            </div>
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </Popup>
                </Marker>
                ))}
            </LayerGroup>
            </LayersControl.Overlay>

            <LayersControl.Overlay checked name="NASA Uydu Verileri">
            <LayerGroup>
                {nasaFireData.map(fire => (
                <Marker key={fire.id} position={[fire.latitude, fire.longitude]} icon={satelliteIcon}>
                    <Popup className="font-sans">
                    <b className="text-slate-800">Uydu Tespiti (NASA)</b> <br />
                    <b className="text-slate-800">Güvenilirlik:</b> <span className="text-slate-600">{getConfidenceText(fire.confidence)}</span>
                    </Popup>
                </Marker>
                ))}
            </LayerGroup>
            </LayersControl.Overlay>
        </LayersControl>

        {weather && risk && (
            <div className="hidden md:flex flex-col absolute top-6 left-6 z-[999] bg-white p-5 rounded-3xl shadow-2xl w-64 border border-slate-100 font-sans">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Bölgesel Durum</h3>
                    {locationName && (
                        <p className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md">
                            📍 {locationName}
                        </p>
                    )}
                </div>

                <div className={`p-4 rounded-2xl text-white text-center mb-4 shadow-lg ${risk.color}`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-90 mb-1">YANGIN RİSKİ</p>
                    <p className="text-2xl font-black tracking-tight">{risk.level}</p>
                    <p className="text-[10px] mt-2 bg-black/20 inline-block px-3 py-1 rounded-full font-bold">Endeks: {risk.score} / 100</p>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center bg-slate-50 p-3 rounded-2xl border border-slate-100">
                    <div className="flex flex-col items-center">
                        <span className="text-slate-400 text-xs mb-1">Sıcaklık</span>
                        <span className="font-black text-slate-700">{Math.round(weather.main.temp)}°</span>
                    </div>
                    <div className="flex flex-col items-center border-x border-slate-200">
                        <span className="text-slate-400 text-xs mb-1">Nem</span>
                        <span className="font-black text-slate-700">%{weather.main.humidity}</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-slate-400 text-xs mb-1">Rüzgar</span>
                        <span className="font-black text-slate-700">{Math.round(weather.wind.speed)}</span>
                    </div>
                </div>
            </div>
        )}
        </MapContainer>
    </div>
  );
};

export default MapComponent;